/**
 * /fraction-calculator — калькулятор дробей с ходом решения, как в тетради.
 *
 * Один движок точной арифметики (BigInt, engine/*) и вкладки вокруг него:
 * калькулятор, сократить, сравнить, общий знаменатель, десятичные, смешанные,
 * НОД и НОК, множители, задачи на дроби, тренажёр. Каждая вкладка строит
 * «лист решения» (ui/doc.js), и страница рисует его в три панели: ответ, ход
 * решения, картинка. Текст для тетради и PDF собираются из того же листа.
 *
 * ⚠️ Запись — школьная и по локали (engine/notation.js): «·» и «:» в русской
 * тетради, «×» и «÷» в американской, «0,(3)» против «0.3̅». Человек может
 * переключить любое в настройках, и выбор помнится (__toolSettings).
 * Испанская страница берёт десятичный знак по стране читателя (языки браузера):
 * Мексика и Центральная Америка — точка, Испания и Южная Америка — запятая.
 *
 * ⚠️ Телеметрия. Решённый пример в телеметрию НЕ уходит — уходят вкладка, вид задачи
 * (какие действия, сколько их, способ) и длина: по ним видно, ЧТО решали. Отвергнутый ввод
 * уходит обрезанным до 60 знаков вместе с кодом отказа (`tool_diagnostic`): это главный
 * прибор «чего люди хотят и чего тул не понял». Событие шлётся, когда ввод «устоялся»
 * (человек перестал печатать), а не на каждый знак: «1/2 +» посреди набора — не отказ.
 */

import { notationFor, decText, OPTIONS } from './engine/notation.js';
import { parse } from './engine/parse.js';
import { E, valElem } from './engine/solve.js';
import { make } from './engine/rational.js';
import { generate, check, rng, TOPICS, answerText } from './engine/trainer.js';
import { similarExpr, similarOne, similarList, similarInts, similarNatural, similarPart } from './engine/similar.js';
import { makeRenderer, esc, ofElems } from './ui/render.js';
import { renderCard, renderSteps, docText } from './ui/doc.js';
import { VIEWS, partDoc, calcDoc } from './ui/views.js';

const CFG = window.__FRC_CFG || {};
const I18N = CFG.i18n || {};
const SLUG = 'fraction-calculator';
const LOCALE = String(window.__LOCALE || 'en').slice(0, 2);
const $ = (id) => document.getElementById(id);
/* Ключа нет — показываем сам ключ: пропуск видно сразу, а не «английским, который выглядит готовым». */
const t = (k) => (I18N[k] != null ? I18N[k] : k);

const TABS = ['calc', 'reduce', 'compare', 'lcd', 'decimal', 'mixed', 'gcd', 'factor', 'part', 'trainer'];
const SETTING_KEYS = ['dec', 'mul', 'div', 'period', 'factor', 'mixed', 'method'];
const MAX_CELLS = 6;

const settings = {};
const state = {
    tab: 'calc',
    src: { calc: '', reduce: '', compare: '', lcd: '', decimal: '', mixed: '', gcd: '', factor: '' },
    part: { kind: 'of', a: '', b: '' },
    model: null,
    reduceMethod: 'euclid',
    gcdMethod: null,
    factorStyle: null,
    doc: null,
    trainer: { topic: 'diff_den', level: 1, task: null, answered: false, shown: false },
};

/* Языки браузера: по ним решается умолчание записи испанской страницы (engine/notation.js). */
const NAV_LANGS = (navigator.languages && navigator.languages.length) ? Array.from(navigator.languages) : [navigator.language || ''];
/* Знак, которым страницу отрисовал сервер: подпись клавиши «Coma decimal» верна только при нём. */
const PAGE_DEC = notationFor(LOCALE).dec;

let nt = notationFor(LOCALE, {}, NAV_LANGS);
let R = makeRenderer({ nt, i18n: I18N });
const ctx = () => ({ t, nt, R, state });

/* ── настройки записи ──────────────────────────────────────────────────── */
const TS = () => window.__toolSettings;
function loadSettings() {
    for (const k of SETTING_KEYS) {
        let v = null;
        try { v = TS() ? TS().load(SLUG, 'nt_' + k, null) : null; } catch (_) { v = null; }
        if (v != null && OPTIONS[k].includes(v)) settings[k] = v;
    }
    try {
        const m = TS() ? TS().load(SLUG, 'model', null) : null;
        if (['pie', 'strip', 'line'].includes(m)) state.model = m;
    } catch (_) { /* настройки не читаются — умолчания */ }
    applyNotation();
}
function applyNotation() {
    nt = notationFor(LOCALE, settings, NAV_LANGS);
    R = makeRenderer({ nt, i18n: I18N });
    document.querySelectorAll('[data-frc-setting]').forEach((sel) => {
        const k = sel.getAttribute('data-frc-setting');
        sel.value = nt[k];
    });
    // Знаки на экранных клавишах — те же, что в записи решения.
    document.querySelectorAll('[data-frc-key-sym]').forEach((b) => {
        const s = b.getAttribute('data-frc-key-sym');
        b.textContent = s === 'mul' ? nt.mul : s === 'div' ? nt.div : s === 'dec' ? nt.dec : b.textContent;
        if (s !== 'dec') return;
        // «Coma decimal» на клавише с точкой — неправда: при другом знаке клавиша зовётся нейтрально.
        if (!b.hasAttribute('data-frc-key-own')) b.setAttribute('data-frc-key-own', b.getAttribute('aria-label') || '');
        const neutral = b.getAttribute('data-frc-key-alt');
        b.setAttribute('aria-label', nt.dec !== PAGE_DEC && neutral ? neutral : b.getAttribute('data-frc-key-own'));
    });
    // Примеры и подсказки — тем же десятичным знаком, что и ответ: чип «0,75» рядом
    // с ответом «0.75» читается как другая запись. Знаки в разметке помечены блейдом.
    document.querySelectorAll('[data-frc-dec]').forEach((el) => { el.textContent = nt.dec; });
    document.querySelectorAll('.frc input[placeholder]').forEach((el) => { el.setAttribute('placeholder', decText(el.getAttribute('placeholder'), nt)); });
    document.querySelectorAll('[data-frc-setting="period"] option').forEach((o) => { o.textContent = decText(o.textContent, nt); });
    document.querySelectorAll('[data-frc-cell-op] option').forEach((o) => {
        if (o.value === '*') o.textContent = nt.mul;
        if (o.value === ':') o.textContent = nt.div;
    });
}
function saveSetting(k, v) {
    settings[k] = v;
    try { if (TS()) TS().save(SLUG, 'nt_' + k, v); } catch (_) { /* приватный режим — живём без памяти */ }
    applyNotation();
    funnel('setting', { k, v });
    refresh(false);
}

/* ── ошибки ────────────────────────────────────────────────────────────── */
function errText(e) {
    const code = (e && e.code) || 'unknown';
    const info = (e && e.info) || {};
    let msg = I18N['err_' + code] != null ? I18N['err_' + code] : t('err_generic');
    msg = decText(msg, nt);            // пример в сообщении («0,(3)») — знаком записи; до подстановки значений
    msg = msg.replace(':max', String(info.max != null ? info.max : '')).replace(':min', String(info.min != null ? info.min : ''))
        .replace(':ch', String(info.ch != null ? info.ch : '')).replace(':digits', String(info.digits != null ? info.digits : ''));
    return msg;
}
/** Сообщение и место ошибки: кусок ввода с подсвеченным знаком. */
function showError(el, input, e, src, soft) {
    if (!el) return;
    el.classList.toggle('soft', !!soft);
    if (!e) {
        el.hidden = true;
        el.innerHTML = '';
        if (input) input.removeAttribute('aria-invalid');
        return;
    }
    let h = esc(errText(e));
    const at = e && typeof e.at === 'number' ? e.at : null;
    if (at != null && src && src.length) {
        const a = Math.max(0, at - 18);
        const pre = src.slice(a, at);
        // «Непонятный знак «abc»» — подсвечено всё слово, а не его первая буква.
        const w = Math.max(1, Math.min(12, e.info && e.info.ch != null ? String(e.info.ch).length : 1));
        const ch = src.slice(at, at + w) || ' ';
        const post = src.slice(at + w, at + w + 17);
        h += '<span class="frc-err-at" dir="ltr"><code>' + (a > 0 ? '…' : '') + esc(pre) + '<mark>' + esc(ch) + '</mark>' + esc(post) + '</code></span>';
    }
    el.innerHTML = h;
    el.hidden = false;
    if (!input) return;
    if (soft) input.removeAttribute('aria-invalid');
    else input.setAttribute('aria-invalid', 'true');
}
/**
 * Пример ещё пишут: «1/2 +» или незакрытая скобка в КОНЦЕ строки, пока поле в фокусе. Это не ошибка,
 * а недописанная строка — подсказка остаётся, красной рамки нет. Enter и уход из поля делают её отказом.
 */
function stillTyping(e, src, input, from) {
    if (!e || !input || document.activeElement !== input) return false;
    if (from !== 'typed' && from !== 'key') return false;
    const end = String(src || '').replace(/\s+$/, '').length;
    return (e.code === 'missing_operand' && typeof e.at === 'number' && e.at >= end) || e.code === 'unclosed';
}

/* ── вывод листа решения ───────────────────────────────────────────────── */
function paintDoc(doc) {
    state.doc = doc;
    const card = $('frcCard');
    const has = !!doc;
    $('frcEmptyWrap').hidden = false;
    $('frcOutWrap').hidden = !has;
    $('frcEmpty').hidden = has;
    if (!has) {
        $('frcStepsWrap').hidden = true;
        $('frcModelWrap').hidden = true;
        return;
    }
    card.innerHTML = renderCard(doc, R, t);
    flash(card);
    const steps = renderSteps(doc, R);
    $('frcSteps').innerHTML = steps;
    $('frcStepsWrap').hidden = !steps;
    paintMethods(doc.methods);
    paintModel();
}

function paintMethods(m) {
    const box = $('frcMethods');
    if (!m || !m.list || m.list.length < 2) { box.hidden = true; box.innerHTML = ''; return; }
    box.innerHTML = m.list.map((x) => '<button type="button" class="tc-seg-b' + (x.id === m.cur ? ' on' : '') + '" role="radio" aria-checked="'
        + (x.id === m.cur ? 'true' : 'false') + '" data-frc-method="' + esc(x.id) + '" data-frc-method-key="' + esc(m.key) + '">' + esc(x.label) + '</button>').join('');
    box.hidden = false;
}

function paintModel() {
    const doc = state.doc;
    const wrap = $('frcModelWrap');
    if (!doc || !doc.modelFor) { wrap.hidden = true; return; }
    const modes = doc.modelModes || ['pie', 'strip', 'line'];
    let mode = state.model && modes.includes(state.model) ? state.model : (doc.modelDefault || modes[0]);
    let html = null;
    try { html = doc.modelFor(mode); } catch (e) { html = null; refusal('model_failed', { tab: state.tab, mode }); }
    if (!html) {
        // Круги не рисуются (отрицательное или большое число) — пробуем прямую.
        for (const m of modes) {
            if (m === mode) continue;
            try { html = doc.modelFor(m); } catch (_) { html = null; }
            if (html) { mode = m; break; }
        }
    }
    if (!html) { wrap.hidden = true; return; }
    document.querySelectorAll('[data-frc-model]').forEach((b) => {
        const m = b.getAttribute('data-frc-model');
        b.hidden = !modes.includes(m);
        b.classList.toggle('on', m === mode);
        b.setAttribute('aria-checked', m === mode ? 'true' : 'false');
    });
    $('frcModel').innerHTML = html;
    wrap.hidden = false;
}

function flash(el) {
    el.classList.remove('tc-flash');
    void el.offsetWidth;          // перезапуск анимации появления
    el.classList.add('tc-flash');
}

/* ── вкладки ───────────────────────────────────────────────────────────── */
function setTab(tab, { focus = false, fromHash = false } = {}) {
    if (!TABS.includes(tab)) tab = 'calc';
    const prev = state.tab;
    state.tab = tab;
    document.querySelectorAll('[data-frc-tab]').forEach((b) => {
        const on = b.getAttribute('data-frc-tab') === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        if (on && focus) b.focus();
    });
    document.querySelectorAll('[data-pane]').forEach((p) => { p.hidden = p.getAttribute('data-pane') !== tab; });
    $('frcApp').setAttribute('data-tab', tab);
    const isTrainer = tab === 'trainer';
    $('frcOutArea').hidden = isTrainer;
    $('frcTrainerArea').hidden = !isTrainer;
    if (isTrainer) {
        if (!state.trainer.task) newTask(false);
        else paintTask();
        $('frcStepsWrap').hidden = !state.trainer.shown;
        $('frcModelWrap').hidden = true;
    } else {
        // Пустая вкладка показывает себя сразу: подставляем первый пример.
        if (tab !== 'part' && !state.src[tab]) {
            const first = document.querySelector('[data-pane="' + tab + '"] [data-frc-sample]');
            if (first) setSrc(tab, decText(first.getAttribute('data-frc-sample'), nt));
        }
        if (tab === 'part' && !state.part.a) {
            const first = document.querySelector('[data-pane="part"] [data-frc-part-sample]');
            if (first) fillPart(first);
        }
        refresh(false);
    }
    if (prev !== tab && !fromHash) {
        funnel('mode', { tab });
        saveHash();
    }
}

function inputFor(tab) {
    return tab === 'calc' ? $('frcExpr') : $('frcIn-' + tab);
}
/**
 * Поле выражения — слева направо (dir=ltr), и в нём число после арабского слова
 * по правилам Unicode прыгает влево: «75% من 4/5» видно как «75% 4/5 من». Невидимая
 * метка LRM после слова возвращает порядок; разбор считает её пробелом, в ссылку
 * она не попадает (noMarks).
 */
const RTL_RUN = /([\u0590-\u08ff\ufb1d-\ufdff\ufe70-\ufefc]+)(?![\u200e\u200f])/g;
const ltrSafe = (v) => String(v).replace(RTL_RUN, '$1\u200e');
const noMarks = (v) => String(v).replace(/[\u200e\u200f\u061c]/g, '');
function setSrc(tab, v) {
    state.src[tab] = noMarks(v);
    const el = inputFor(tab);
    if (el) el.value = ltrSafe(v);
    if (tab === 'calc') syncCellsFromString();
}

/* ── пересчёт ──────────────────────────────────────────────────────────── */
/** Вкладка, на которой сейчас висит мягкая подсказка «пример не дописан» (см. stillTyping). */
let softTab = '';
/**
 * @param {false|string} from  откуда ввод: 'typed' | 'enter' | 'key' | 'cells' | 'sample' | 'link' | 'again';
 *                             false — перерисовка без действия человека (смена записи, первый показ)
 */
function refresh(from) {
    const tab = state.tab;
    if (tab === 'trainer') return;
    const errEl = tab === 'calc' ? $('frcErr') : $('frcErr-' + tab);
    const input = tab === 'part' ? null : inputFor(tab);
    let doc = null;
    let src;
    try {
        if (tab === 'part') {
            src = state.part.a + '|' + state.part.b;
            if (!state.part.a.trim() || !state.part.b.trim()) { showError(errEl, null, null); paintDoc(null); saveHash(); settle(null); return; }
            doc = partDoc(state.part.kind, state.part.a, state.part.b, ctx());
        } else {
            src = state.src[tab];
            if (!src.trim()) { showError(errEl, input, null); paintDoc(null); saveHash(); settle(null); return; }
            doc = VIEWS[tab](src, ctx());
        }
    } catch (e) {
        const known = !!(e && e.name === 'FracError');
        const field = tab === 'part' ? (e && e.info && e.info.field === 1 ? $('frcPartA') : $('frcPartB')) : input;
        const soft = known && tab !== 'part' && stillTyping(e, src, input, from);
        softTab = soft ? tab : '';
        showError(errEl, field, e, tab === 'part' ? '' : src, soft);
        paintDoc(null);
        // Отказ написан у поля; пустая карточка ответа под ним выглядела поломкой.
        $('frcEmptyWrap').hidden = true;
        saveHash();           // ссылка и перезагрузка возвращают то, что набрано, а не прошлый пример
        if (!known) {
            // Не отказ ввода, а наша ошибка: в телеметрию — место и текст, человеку — фраза у поля.
            if (window.__handleError) window.__handleError(e, 'frc_refresh', { say: t('err_generic') });
            else console.error('[fraction-calculator]', e);
            settle(null);
            return;
        }
        if (from) {
            stage('input', { tab });
            // Недописанный пример посреди набора — не отказ: в «чего люди хотят» он не идёт.
            if (soft) { settle(null); return; }
            settle({ kind: 'refused', sig: tab + '|' + e.code + '|' + src, code: e.code, tab, at: e.at, len: String(src || '').length, input: src, from }, from);
        }
        return;
    }
    showError(errEl, input, null);
    if (tab === 'part') { $('frcPartA').removeAttribute('aria-invalid'); $('frcPartB').removeAttribute('aria-invalid'); }
    paintDoc(doc);
    saveHash();
    if (from) {
        stage('input', { tab });
        const res = doc.res;
        settle({
            kind: 'ok', sig: tab + '|' + src,
            tab,
            task: tab === 'part' ? state.part.kind : (doc.kind || tab),
            actions: res ? res.actions.length : 0,
            ops: res ? res.actions.map((a) => a.op).join('') : '',
            conv: res ? res.conversions.length : 0,
            method: doc.methods ? doc.methods.cur : '',
            len: String(src || '').length,
            from,
        }, from);
    }
}

/* ── клетки «как в тетради» ⇄ строка ───────────────────────────────────── */
/**
 * Клетки — это цепочка «число знак число …» без скобок, десятичных и
 * процентов. Строку, которую клетками не записать, клетки честно не
 * показывают (подпись объясняет почему), а строка остаётся главной.
 */
function chainOf(src) {
    let tree;
    // Строка с ошибкой — не «пример, который клетками не записать»: клетки остаются как были.
    try { tree = parse(src); } catch (_) { return { broken: true }; }
    const out = [];
    let ok = true;
    (function walk(n) {
        if (!ok) return;
        if (n.type === 'bin' && !n.implicit && !n.of) {
            walk(n.left);
            out.push({ op: n.op });
            walk(n.right);
            return;
        }
        if (n.type === 'lit' && (n.form === 'int' || n.form === 'frac' || n.form === 'mixed')) {
            out.push({ w: n.form === 'int' ? n.int : (n.whole || ''), n: n.num || '', d: n.den || '' });
            return;
        }
        ok = false;
    }(tree));
    if (!ok) return null;
    // В строке без скобок порядок элементов дерева совпадает с набранным.
    const ops = out.filter((x) => x.op).map((x) => x.op);
    const operands = out.filter((x) => !x.op);
    if (operands.length > MAX_CELLS) return null;
    return { operands, ops };
}

let cells = { operands: [{ w: '', n: '', d: '' }, { w: '', n: '', d: '' }], ops: ['+'] };

function cellHtml(o, i) {
    const lab = (k) => esc(t(k).replace(':n', String(i + 1)));
    return '<div class="frc-cell" data-i="' + i + '">'
        + '<input class="frc-cw no-field-floor" placeholder=" " inputmode="numeric" enterkeyhint="next" autocomplete="off" maxlength="12" aria-label="' + lab('cell_whole') + '" data-k="w" value="' + esc(o.w) + '">'
        + '<span class="frc-cf"><input class="frc-cn no-field-floor" inputmode="numeric" enterkeyhint="next" autocomplete="off" maxlength="12" aria-label="' + lab('cell_num') + '" data-k="n" value="' + esc(o.n) + '">'
        + '<input class="frc-cd no-field-floor" inputmode="numeric" enterkeyhint="next" autocomplete="off" maxlength="12" aria-label="' + lab('cell_den') + '" data-k="d" value="' + esc(o.d) + '"></span>'
        // Убрать можно любое число, пока их больше двух: подсказка «заполните или уберите» касается и второго.
        + (cells.operands.length > 2 ? '<button type="button" class="frc-cx-del" data-del="' + i + '" aria-label="' + lab('cell_remove') + '">×</button>' : '')
        + '</div>';
}
function opSelHtml(op, i) {
    const opts = [['+', '+'], ['-', '−'], ['*', nt.mul], [':', nt.div]];
    return '<select class="frc-cop no-field-floor" data-frc-cell-op data-oi="' + i + '" aria-label="' + esc(t('cell_op').replace(':n', String(i + 1))) + '">'
        + opts.map(([v, s]) => '<option value="' + v + '"' + (v === op ? ' selected' : '') + '>' + esc(s) + '</option>').join('') + '</select>';
}
function paintCells() {
    const box = $('frcCells');
    let h = '';
    cells.operands.forEach((o, i) => {
        if (i) h += opSelHtml(cells.ops[i - 1], i - 1);
        h += cellHtml(o, i);
    });
    box.innerHTML = h;
    $('frcAdd').disabled = cells.operands.length >= MAX_CELLS;
}
const cellEmpty = (o) => !o.w.trim() && !o.n.trim() && !o.d.trim();
/**
 * Сколько чисел набрано: пустые клетки В КОНЦЕ не считаются. «17/5» и нетронутая вторая
 * дробь — это одно число, а не «заполните число 2»: иначе правка клетки не меняла ни
 * строку, ни ответ, пока человек не заполнит дробь, которая ему не нужна.
 */
function cellsUsed() {
    let n = cells.operands.length;
    while (n > 0 && cellEmpty(cells.operands[n - 1])) n--;
    return n;
}
function cellsToString() {
    const parts = [];
    const used = cellsUsed();
    for (let i = 0; i < used; i++) {
        const o = cells.operands[i];
        const w = o.w.trim();
        const n = o.n.trim();
        const d = o.d.trim();
        let s = '';
        if (n || d) s = (w ? w + ' ' : '') + (n || '') + '/' + (d || '');
        else s = w;
        if (i) parts.push({ '+': '+', '-': '-', '*': '*', ':': ':' }[cells.ops[i - 1]]);
        parts.push(s);
    }
    return parts.join(' ');
}
/** Незаполненная клетка — подсказка, а не ошибка разбора «пропущено число». */
function cellsGap() {
    const used = cellsUsed();
    for (let i = 0; i < used; i++) {
        const o = cells.operands[i];
        const w = o.w.trim(); const n = o.n.trim(); const d = o.d.trim();
        for (const [k, v] of [['w', w], ['n', n], ['d', d]]) if (v && !/^\d+$/.test(v)) return { i, k, key: 'cells_digits' };
        if (!w && !n && !d) return { i, k: 'n', key: 'cells_empty' };
        if (n && !d) return { i, k: 'd', key: 'cells_need_den' };
        if (!n && d) return { i, k: 'n', key: 'cells_need_num' };
        if (d && /^0+$/.test(d)) return { i, k: 'd', key: 'err_zero_denominator' };
    }
    return null;
}
function onCellsInput() {
    const gap = cellsGap();
    const hint = $('frcCellsHint');
    document.querySelectorAll('#frcCells input').forEach((el) => el.removeAttribute('aria-invalid'));
    if (gap) {
        hint.textContent = t(gap.key).replace(':n', String(gap.i + 1));
        hint.hidden = false;
        const el = document.querySelector('#frcCells .frc-cell[data-i="' + gap.i + '"] [data-k="' + gap.k + '"]');
        if (el && gap.key !== 'cells_empty') el.setAttribute('aria-invalid', 'true');
        return;
    }
    hint.hidden = true;
    const s = cellsToString();
    state.src.calc = s;
    $('frcExpr').value = s;
    refresh('cells');
}
function syncCellsFromString() {
    const ch = chainOf(state.src.calc);
    const box = $('frcCellsBox');
    if (!state.src.calc.trim()) {
        cells = { operands: [{ w: '', n: '', d: '' }, { w: '', n: '', d: '' }], ops: ['+'] };
        paintCells();
        $('frcCellsNote').hidden = true;
        box.classList.remove('off');
        return;
    }
    if (ch && ch.broken) return;
    if (!ch || ch.operands.length < 1) {
        $('frcCellsNote').hidden = false;
        box.classList.add('off');
        return;
    }
    $('frcCellsNote').hidden = true;
    box.classList.remove('off');
    const same = ch.operands.length === cells.operands.length && ch.ops.every((o, i) => o === cells.ops[i]);
    cells = { operands: ch.operands.length === 1 ? [ch.operands[0], { w: '', n: '', d: '' }] : ch.operands, ops: ch.ops.length ? ch.ops : ['+'] };
    if (same) {
        // Та же форма — меняем значения, не пересоздавая поля (фокус и каретка остаются).
        document.querySelectorAll('#frcCells .frc-cell').forEach((c) => {
            const o = cells.operands[+c.getAttribute('data-i')];
            if (!o) return;
            c.querySelectorAll('input').forEach((el) => { if (document.activeElement !== el) el.value = o[el.getAttribute('data-k')]; });
        });
    } else paintCells();
    $('frcCellsHint').hidden = true;
}

/* ── «ещё такой же пример» ──────────────────────────────────────────────── */
/** Те же действия и виды чисел, другие числа; для вкладок с одним полем — число или список того же склада. */
function similarFor(tab, cur, r) {
    if (tab === 'calc') return similarExpr(cur, r, nt);
    if (tab === 'gcd') return similarInts(cur, r);
    if (tab === 'factor') return similarNatural(cur, r);
    if (tab === 'compare' || tab === 'lcd') return similarList(cur, r);
    return similarOne(cur, r);
}
function again() {
    const tab = state.tab;
    if (tab === 'trainer') return;
    const r = rng(seed());
    let done = false;
    if (tab === 'part') {
        let p = state.part.a.trim() && state.part.b.trim() ? similarPart(state.part.kind, state.part.a, state.part.b, r) : null;
        if (!p) {
            const first = document.querySelector('[data-frc-part-sample][data-kind="' + state.part.kind + '"]');
            if (first) p = similarPart(state.part.kind, decText(first.getAttribute('data-a'), nt), decText(first.getAttribute('data-b'), nt), r);
        }
        if (p) { state.part.a = p.a; state.part.b = p.b; paintPart(); done = true; }
    } else {
        let neu = similarFor(tab, state.src[tab] || '', r);
        if (!neu) {
            // Поле пустое или с ошибкой — образцом служит первый пример вкладки.
            const first = document.querySelector('[data-pane="' + tab + '"] [data-frc-sample]');
            if (first) neu = similarFor(tab, decText(first.getAttribute('data-frc-sample'), nt), r);
        }
        if (neu) { setSrc(tab, neu); done = true; }
    }
    funnel('again', { tab, ok: done ? 1 : 0 });
    if (done) refresh('again');
}

/* ── недавние примеры ──────────────────────────────────────────────────── */
/**
 * Последние решённые примеры вкладки — чипами под готовыми примерами: домашнее задание
 * решают подряд, и к прошлому примеру возвращаются. Хранится только в этом браузере.
 */
const RECENT_MAX = 6;
const RECENT_LEN = 60;
let recent = {};
function loadRecent() {
    try {
        const v = TS() ? TS().load(SLUG, 'recent', null) : null;
        if (v && typeof v === 'object' && !Array.isArray(v)) recent = v;
    } catch (_) { recent = {}; }
}
function sampleSet(tab) {
    const out = new Set();
    document.querySelectorAll('[data-pane="' + tab + '"] [data-frc-sample]').forEach((b) => out.add(decText(b.getAttribute('data-frc-sample'), nt)));
    return out;
}
function paintRecent(tab) {
    const box = document.querySelector('[data-frc-recent="' + tab + '"]');
    if (!box) return;
    const skip = sampleSet(tab);
    const list = (Array.isArray(recent[tab]) ? recent[tab] : []).filter((x) => typeof x === 'string' && !skip.has(x));
    box.hidden = !list.length;
    // черта над периодом — той же .frc-bar, что в готовых примерах
    const show = (v) => esc(v).replace(/(?:\d\u0305)+/g, (m) => '<span class="frc-bar">' + m.replace(/\u0305/g, '') + '</span>');
    box.querySelector('[data-frc-recent-list]').innerHTML = list.map((v) => '<button type="button" class="tc-chip frc-mono" dir="ltr" data-frc-recent-item="'
        + esc(v) + '">' + show(ltrSafe(v)) + '</button>').join('');
}
function pushRecent(tab, src) {
    const box = document.querySelector('[data-frc-recent="' + tab + '"]');
    const v = noMarks(String(src || '')).trim();
    if (!box || !v || v.length > RECENT_LEN) return;
    // «3 1/4 - 1», на котором человек задумался, а потом дописал «2/3», — не отдельный пример: начало уходит
    const list = (Array.isArray(recent[tab]) ? recent[tab] : []).filter((x) => x !== v && v.indexOf(x) !== 0);
    list.unshift(v);
    recent[tab] = list.slice(0, RECENT_MAX);
    try { if (TS()) TS().save(SLUG, 'recent', recent); } catch (_) { /* без памяти — только на эту вкладку */ }
    paintRecent(tab);
}

/* ── ссылка ────────────────────────────────────────────────────────────── */
function saveHash() {
    const q = new URLSearchParams();
    q.set('t', state.tab);
    if (state.tab === 'part') { q.set('k', state.part.kind); q.set('a', state.part.a); q.set('b', state.part.b); }
    else if (state.tab === 'trainer') { q.set('topic', state.trainer.topic); q.set('lv', String(state.trainer.level)); }
    else if (state.src[state.tab]) q.set('v', noMarks(state.src[state.tab]));
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}
function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    const tab = q.get('t');
    if (!TABS.includes(tab)) return false;
    if (tab === 'part') {
        const k = q.get('k');
        state.part = { kind: ['of', 'whole', 'ratio'].includes(k) ? k : 'of', a: q.get('a') || '', b: q.get('b') || '' };
        paintPart();
    } else if (tab === 'trainer') {
        const topic = q.get('topic');
        if (TOPICS.includes(topic)) state.trainer.topic = topic;
        const lv = parseInt(q.get('lv'), 10);
        if (lv >= 1 && lv <= 3) state.trainer.level = lv;
        paintTrainerControls();
    } else {
        const v = q.get('v');
        if (v != null) setSrc(tab, v);
    }
    setTab(tab, { fromHash: true });
    return true;
}

/* ── задачи на дроби ───────────────────────────────────────────────────── */
function paintPart() {
    document.querySelectorAll('[data-frc-part]').forEach((b) => {
        const on = b.getAttribute('data-frc-part') === state.part.kind;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    $('frcPartA').value = state.part.a;
    $('frcPartB').value = state.part.b;
    $('frcPartLA').textContent = t('pt_' + state.part.kind + '_a');
    $('frcPartLB').textContent = t('pt_' + state.part.kind + '_b');
    document.querySelectorAll('[data-frc-part-sample]').forEach((b) => { b.hidden = b.getAttribute('data-kind') !== state.part.kind; });
}
function fillPart(btn) {
    state.part = { kind: btn.getAttribute('data-kind'), a: decText(btn.getAttribute('data-a'), nt), b: decText(btn.getAttribute('data-b'), nt) };
    paintPart();
}

/* ── тренажёр ──────────────────────────────────────────────────────────── */
const STATS_KEY = 'frc_trainer_stats_v1';
function loadStats() {
    try { return JSON.parse(localStorage.getItem(STATS_KEY) || '{}') || {}; } catch (_) { return {}; }
}
function saveStats(s) {
    try { localStorage.setItem(STATS_KEY, JSON.stringify(s)); } catch (_) { /* без памяти — только на эту вкладку */ }
}
function seed() {
    try { return crypto.getRandomValues(new Uint32Array(1))[0]; } catch (_) { return Math.floor(Math.random() * 4294967295); }
}
function paintTrainerControls() {
    $('frcTopic').value = state.trainer.topic;
    document.querySelectorAll('[data-frc-level]').forEach((b) => {
        const on = +b.getAttribute('data-frc-level') === state.trainer.level;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
}
function newTask(user) {
    const tr = state.trainer;
    tr.task = generate(tr.topic, tr.level, rng(seed()));
    tr.answered = false;
    tr.shown = false;
    tr.right = false;
    paintTask();
    $('frcStepsWrap').hidden = true;
    $('frcModelWrap').hidden = true;
    if (user) funnel('trainer_start', { topic: tr.topic, lv: tr.level });
    saveHash();
    // С клавиатуры ответ печатают сразу; на телефоне поле не трогаем — экранная клавиатура закрыла бы пример.
    let fine = true;
    try { fine = !window.matchMedia('(pointer:coarse)').matches; } catch (_) { fine = true; }
    if (user && fine) $('frcAn').focus({ preventScroll: true });
}
function taskElems(task) {
    if (task.topic === 'part') return ofElems(t('of_fmt'), [E.fr(task.part.n, task.part.d)], [E.num(task.part.N)]);
    try { return calcDoc(task.src, ctx()).head.elems; } catch (_) { return [{ t: 'txt', v: task.src }]; }
}
function paintTask() {
    const tr = state.trainer;
    const task = tr.task;
    if (!task) return;
    $('frcTaskQ').textContent = t('tr_q_' + task.topic);
    const th = R.html(taskElems(task));
    $('frcTask').innerHTML = th + '<span class="frc-op">=</span>';
    $('frcTask').classList.toggle('has-an', !!R.rowClass(th));
    ['frcAw', 'frcAn', 'frcAd'].forEach((id) => { $(id).value = ''; $(id).removeAttribute('aria-invalid'); });
    $('frcAneg').checked = false;
    $('frcTrMsg').hidden = true;
    $('frcTrShow').hidden = false;
    $('frcTrNext').hidden = true;
    paintStats();
}
function readAnswer() {
    return { neg: $('frcAneg').checked, w: $('frcAw').value, n: $('frcAn').value, d: $('frcAd').value };
}
function checkTask() {
    const tr = state.trainer;
    if (!tr.task) return;
    const res = check(tr.task, readAnswer(), { strict: $('frcStrict').checked });
    const msg = $('frcTrMsg');
    const st = res.status;
    const good = st === 'right';
    const soft = ['empty', 'digits', 'half', 'zero_den', 'not_reduced', 'need_mixed', 'need_improper'].includes(st);
    msg.className = 'tc-msg ' + (good ? 'good' : soft ? 'warn' : 'bad');
    let text = t('tr_' + st);
    if (good && res.hint) text += ' ' + t('tr_hint_' + res.hint);
    if (st === 'wrong') text += ' ' + t('tr_answer_is').replace(':a', answerText(tr.task.answer).replace('-', '−').replace('/', '/'));
    msg.textContent = text;
    msg.hidden = false;
    if (['empty', 'digits', 'half', 'zero_den'].includes(st)) return;
    // Первый ответ на пример идёт в статистику; повторные попытки — нет.
    // «Верно, но сократите» — ещё не ответ: попытка продолжается.
    if (!tr.answered && (good || st === 'wrong')) {
        const s = loadStats();
        const k = tr.topic;
        s[k] = s[k] || { r: 0, w: 0 };
        if (good) s[k].r++; else if (st === 'wrong') s[k].w++;
        saveStats(s);
        tr.answered = true;
        paintStats();
    }
    // Пример закрыт (верно или показано решение) — «Новый пример» прямо у ответа.
    tr.right = good;
    if (good || st === 'wrong') $('frcTrNext').hidden = false;
    const total = loadStats()[tr.topic] || { r: 0, w: 0 };
    funnel('trainer_answer', { topic: tr.topic, lv: tr.level, status: st, strict: $('frcStrict').checked ? 1 : 0, right: total.r || 0, wrong: total.w || 0 });
    if (st === 'wrong') showSolution(true);
}
function showSolution(auto) {
    const tr = state.trainer;
    if (!tr.task) return;
    tr.shown = true;
    let doc;
    try {
        doc = tr.task.topic === 'reduce' ? VIEWS.reduce(tr.task.src, ctx())
            : tr.task.topic === 'to_mixed' || tr.task.topic === 'to_improper' ? VIEWS.mixed(tr.task.src, ctx())
                : tr.task.topic === 'dec' ? VIEWS.decimal(tr.task.src, ctx())
                    : tr.task.topic === 'part' ? partDoc('of', tr.task.part.n + '/' + tr.task.part.d, String(tr.task.part.N), ctx())
                        : calcDoc(tr.task.src, ctx());
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'frc_trainer_solution', { say: t('err_generic') });
        return;
    }
    state.doc = doc;
    $('frcSteps').innerHTML = renderSteps(doc, R);
    paintMethods(null);
    $('frcStepsWrap').hidden = false;
    if (!auto) funnel('trainer_show', { topic: tr.topic, lv: tr.level });
}
function paintStats() {
    const s = loadStats();
    const rows = TOPICS.filter((k) => s[k] && (s[k].r || s[k].w));
    const box = $('frcStats');
    if (!rows.length) { box.innerHTML = '<p class="tc-hint">' + esc(t('tr_stats_empty')) + '</p>'; return; }
    let h = '<table class="tc-table"><thead><tr><th>' + esc(t('tr_topic')) + '</th><th class="num">✓</th><th class="num">✗</th><th class="num">%</th></tr></thead><tbody>';
    for (const k of rows) {
        const r = s[k].r || 0;
        const w = s[k].w || 0;
        h += '<tr' + (k === state.trainer.topic ? ' class="now"' : '') + '><td>' + esc(t('tr_topic_' + k)) + '</td><td class="num">' + r + '</td><td class="num">' + w
            + '</td><td class="num">' + Math.round((100 * r) / Math.max(1, r + w)) + '</td></tr>';
    }
    box.innerHTML = h + '</tbody></table>';
}

/* ── выдача: копировать, PDF, печать, ссылка, лист примеров ─────────────── */
async function copySolution() {
    const doc = state.doc;
    if (!doc) return;
    const text = docText(doc, R, t);
    const ok = window.__copyText ? await window.__copyText(text) : false;
    if (window.__toast) window.__toast(ok ? t('copied') : t('copy_failed'), ok ? 'success' : 'error');
    funnel('export', { kind: 'copy', tab: state.tab, ok: ok ? 1 : 0, len: text.length });
}
async function copyForm(btn) {
    const text = btn.getAttribute('data-copy') || '';
    const ok = window.__copyText ? await window.__copyText(text) : false;
    if (window.__toast) window.__toast(ok ? t('copied_value').replace(':v', text) : t('copy_failed'), ok ? 'success' : 'error');
    ae('copy_form', { tab: state.tab });
}
async function share() {
    saveHash();
    const url = location.href;
    if (navigator.share) {
        try {
            await navigator.share({ title: document.title, url });
            funnel('share', { via: 'native', tab: state.tab });
            return;
        } catch (e) {
            if (e && e.name === 'AbortError') return;       // передумал — это не ошибка
        }
    }
    const ok = window.__copyText ? await window.__copyText(url) : false;
    if (window.__toast) window.__toast(ok ? t('link_copied') : t('copy_failed'), ok ? 'success' : 'error');
    funnel('share', { via: 'clipboard', tab: state.tab, ok: ok ? 1 : 0 });
}
function printSheet() {
    funnel('export', { kind: 'print', tab: state.tab });
    window.print();
}
function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app frc frc-report';
    root.style.cssText = 'padding:0;max-width:none';
    const card = $('frcCard').cloneNode(true);
    card.removeAttribute('id');
    card.querySelectorAll('button').forEach((b) => b.removeAttribute('data-copy'));
    root.appendChild(card);
    if (!$('frcStepsWrap').hidden) {
        const h = document.createElement('p');
        h.className = 'frc-block-t';
        h.textContent = t('steps_title');
        root.appendChild(h);
        const st = $('frcSteps').cloneNode(true);
        st.removeAttribute('id');
        root.appendChild(st);
    }
    if (!$('frcModelWrap').hidden) {
        const m = $('frcModel').cloneNode(true);
        m.removeAttribute('id');
        root.appendChild(m);
    }
    return root;
}
/** Сборщик PDF не загрузился ни с основного, ни с запасного адреса: кнопка не должна молчать. */
function pdfMissing(kind) {
    if (window.__generatePDF) return false;
    if (window.__toast) window.__toast(t(kind === 'worksheet' ? 'ws_error' : 'pdf_error'), 'error');
    refusal('pdf_lib_missing', { tab: state.tab, kind });
    return true;
}
function savePdf() {
    if (!state.doc || pdfMissing('pdf')) return;
    const btn = $('frcPdf');
    btn.disabled = true;
    window.__generatePDF({
        element: buildReport(),
        title: t('pdf_title'),
        filename: fileName(t('pdf_file')),
        strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        btn.disabled = false;
        funnel('export', { kind: 'pdf', tab: state.tab, ok: r && r.ok ? 1 : 0, code: (r && r.code) || '' });
    }, (e) => {
        btn.disabled = false;
        if (window.__handleError) window.__handleError(e, 'frc_pdf', { say: t('pdf_error') });
    });
}
function fileName(base) {
    try { if (window.__outputName) return window.__outputName.join(window.__outputName.sanitize(base), 'pdf'); } catch (_) { /* примитив не загрузился */ }
    return 'fractions.pdf';
}

/** Лист примеров: варианты и ключ ответов, записью как в тетради. */
function makeWorksheet() {
    if (pdfMissing('worksheet')) return;
    const topic = $('frcWsTopic').value;
    const level = parseInt($('frcWsLevel').value, 10) || 1;
    const count = Math.max(4, Math.min(30, parseInt($('frcWsCount').value, 10) || 12));
    const variants = Math.max(1, Math.min(4, parseInt($('frcWsVariants').value, 10) || 2));
    const withKey = $('frcWsKey').checked;
    const base = seed();
    const sheet = document.createElement('div');
    sheet.className = 'lg-app frc frc-sheet';
    const keys = [];
    for (let v = 0; v < variants; v++) {
        const seen = new Set();
        const list = [];
        const r = rng(base + v * 7919);
        for (let j = 0; list.length < count && j < count * 25; j++) {
            const g = generate(topic, level, r);
            if (seen.has(g.src)) continue;
            seen.add(g.src);
            list.push(g);
        }
        const page = document.createElement('section');
        page.className = 'frc-ws-page';
        if (v) page.style.cssText = 'page-break-before:always;break-before:page';
        page.innerHTML = '<div class="frc-ws-top"><b>' + esc(t('ws_heading')) + '</b><span>' + esc(t('ws_variant').replace(':n', String(v + 1))) + '</span></div>'
            + '<p class="frc-ws-name">' + esc(t('ws_name')) + ': ________________________________</p>'
            + '<p class="frc-ws-task">' + esc(t('tr_q_' + topic)) + '</p>';
        const ol = document.createElement('ol');
        ol.className = 'frc-ws-list';
        list.forEach((g) => {
            const li = document.createElement('li');
            const gh = R.html(taskElems(g));
            li.innerHTML = '<span class="frc-math' + R.rowClass(gh) + '" dir="ltr">' + gh + '<span class="frc-op">=</span></span>';
            ol.appendChild(li);
        });
        page.appendChild(ol);
        sheet.appendChild(page);
        keys.push(list);
    }
    if (withKey) {
        const key = document.createElement('section');
        key.className = 'frc-ws-page';
        key.style.cssText = 'page-break-before:always;break-before:page';
        let h = '<div class="frc-ws-top"><b>' + esc(t('ws_key')) + '</b></div>';
        keys.forEach((list, v) => {
            h += '<p class="frc-ws-kv">' + esc(t('ws_variant').replace(':n', String(v + 1))) + '</p><ol class="frc-ws-keys">';
            list.forEach((g) => { h += '<li><span class="frc-math" dir="ltr">' + R.html([valElem(make(g.answer.n, g.answer.d), { mixed: nt.mixed === 'first' || g.require === 'mixed' })]) + '</span></li>'; });
            h += '</ol>';
        });
        key.innerHTML = h;
        sheet.appendChild(key);
    }
    const btn = $('frcWsMake');
    btn.disabled = true;
    // Плотность и потолок холста решает общий генератор (report-pdf.js): длинный лист он снимает
    // полосами под потолок устройства — своя формула масштаба здесь больше не нужна (16.10.2026).
    window.__generatePDF({
        element: sheet,
        filename: fileName(t('ws_file')),
        strings: { libError: t('ws_error'), genError: t('ws_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        btn.disabled = false;
        funnel('export', { kind: 'worksheet', ok: r && r.ok ? 1 : 0, code: (r && r.code) || '', topic, level, count, variants, key: withKey ? 1 : 0 });
    }, (e) => {
        btn.disabled = false;
        if (window.__handleError) window.__handleError(e, 'frc_worksheet', { say: t('ws_error') });
    });
}

/* ── телеметрия ────────────────────────────────────────────────────────── */
/**
 * Стадии: input (человек начал вводить) → complete (ответ показан; ставит __aeToolComplete) либо
 * refused (ввод отвергнут и так оставлен). Новая правка после ответа — снова input: по последней
 * стадии на уходе видно, где человек остановился.
 */
let lastStage = '';
let completedAt = 0;
function stage(name, meta) {
    if (lastStage === name) return;
    // «complete» общий слой ставит с задержкой (склейка 0,8 с): правка в эту секунду ещё идёт в счёт прошлого результата
    if (name === 'input' && Date.now() - completedAt < 1000) return;
    lastStage = name;
    try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) { /* телеметрия не мешает работе */ }
}
/** Мелкие действия (клавиша, клетка, картинка, способ записи шага). */
function ae(action, p) {
    try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, p || {})); } catch (_) { /* телеметрия не мешает работе */ }
}
/** Ключевые действия — в воронку тула: режим, пример, выдача каждого вида, ссылка, настройка, тренажёр. */
function funnel(name, p) {
    try { if (window.__aeFunnel) window.__aeFunnel(name, p || {}); } catch (_) { /* телеметрия не мешает работе */ }
}
/** Ответ показан — один раз на результат; по полям видно, ЧТО решали, самого примера нет. */
function complete(p) {
    lastStage = 'complete';       // стадию «complete» ставит сам __aeToolComplete
    completedAt = Date.now();
    try { if (window.__aeToolComplete) window.__aeToolComplete(p); } catch (_) { /* телеметрия не мешает работе */ }
}
/** Отказ ввода и «не умею»: код, место и сам отвергнутый ввод (до 60 знаков). Один раз на вид отказа и ввод. */
const refusedOnce = new Set();
function refusal(code, p, input) {
    const key = code + '|' + (p && p.tab) + '|' + String(input == null ? '' : input);
    if (refusedOnce.has(key)) return;
    refusedOnce.add(key);
    const data = Object.assign({ issue: 'frc_' + code }, p || {});
    if (input != null) data.input = noMarks(String(input)).slice(0, 60);
    try { if (window.__aeDiagnostic) window.__aeDiagnostic(data); } catch (_) { /* телеметрия не мешает работе */ }
}
/**
 * Ввод «устоялся»: человек перестал печатать и смотрит на ответ или на отказ. До этого ничего не
 * шлём — «1/2 +» посреди набора иначе уходило бы отказом, а «1/2» — решённым примером.
 * Нажатие (пример-чип, Enter, клавиша «ещё пример») устаивается сразу.
 */
const SETTLE_MS = 1500;
let settleTimer = 0;
let lastSettled = '';
function settle(ev, from) {
    clearTimeout(settleTimer);
    if (!ev) return;
    const fire = () => {
        if (ev.sig === lastSettled) return;
        lastSettled = ev.sig;
        if (ev.kind === 'ok') {
            complete({ via: 'answer', tab: ev.tab, task: ev.task, actions: ev.actions, ops: ev.ops, conv: ev.conv, method: ev.method, len: ev.len, by: ev.from });
            if (ev.from !== 'sample' && ev.tab !== 'part') pushRecent(ev.tab, state.src[ev.tab]);
        } else {
            stage('refused', { tab: ev.tab, code: ev.code });
            refusal(ev.code, { tab: ev.tab, at: ev.at, len: ev.len, by: ev.from }, ev.input);
        }
    };
    if (from === 'sample' || from === 'enter' || from === 'again' || from === 'recent') fire();
    else settleTimer = setTimeout(fire, SETTLE_MS);
}

/* ── запуск ────────────────────────────────────────────────────────────── */
function insertAtCaret(field, ins) {
    const a = field.selectionStart == null ? field.value.length : field.selectionStart;
    const z = field.selectionEnd == null ? a : field.selectionEnd;
    field.value = field.value.slice(0, a) + ins + field.value.slice(z);
    const pos = a + ins.length;
    try { field.setSelectionRange(pos, pos); } catch (_) { /* поле без выделения */ }
    field.focus();
}

function init() {
    const expr = $('frcExpr');
    if (!expr) return;
    loadSettings();

    // вкладки: мышь и стрелки (роль tablist)
    const tabBtns = Array.from(document.querySelectorAll('[data-frc-tab]'));
    tabBtns.forEach((b) => {
        b.addEventListener('click', () => setTab(b.getAttribute('data-frc-tab')));
        b.addEventListener('keydown', (e) => {
            const i = tabBtns.indexOf(b);
            let j = null;
            const rtl = document.documentElement.dir === 'rtl';
            if (e.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) j = (i + 1) % tabBtns.length;
            if (e.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) j = (i - 1 + tabBtns.length) % tabBtns.length;
            if (e.key === 'Home') j = 0;
            if (e.key === 'End') j = tabBtns.length - 1;
            if (j == null) return;
            e.preventDefault();
            setTab(tabBtns[j].getAttribute('data-frc-tab'), { focus: true });
        });
    });

    // строка калькулятора и поля вкладок
    let timer = 0;
    const onType = (tab, el) => () => {
        state.src[tab] = el.value;
        clearTimeout(timer);
        timer = setTimeout(() => { if (tab === 'calc') syncCellsFromString(); refresh('typed'); }, 280);
    };
    const onEnter = (tab) => (e) => {
        if (e.key !== 'Enter') return;
        clearTimeout(timer);
        if (tab === 'calc') syncCellsFromString();
        refresh('enter');
        // Enter на телефоне — «Готово»: клавиатура уходит и открывает ответ под полем.
        try { if (window.matchMedia('(pointer:coarse)').matches) e.target.blur(); } catch (_) { /* старый браузер */ }
    };
    expr.addEventListener('input', onType('calc', expr));
    expr.addEventListener('keydown', onEnter('calc'));
    const onBlur = (tab) => () => { if (softTab === tab && state.tab === tab) refresh(false); };
    expr.addEventListener('blur', onBlur('calc'));
    for (const tab of ['reduce', 'compare', 'lcd', 'decimal', 'mixed', 'gcd', 'factor']) {
        const el = $('frcIn-' + tab);
        if (!el) continue;
        el.addEventListener('input', onType(tab, el));
        el.addEventListener('keydown', onEnter(tab));
        el.addEventListener('blur', onBlur(tab));
    }

    // экранные знаки — в место каретки
    document.querySelectorAll('[data-frc-key]').forEach((b) => {
        b.addEventListener('mousedown', (e) => e.preventDefault());       // фокус остаётся в поле
        b.addEventListener('click', () => {
            let ins = b.getAttribute('data-frc-key');
            if (ins === 'mul') ins = ' ' + nt.mul + ' ';
            if (ins === 'div') ins = ' ' + nt.div + ' ';
            if (ins === 'dec') ins = nt.dec;
            insertAtCaret(expr, ins);
            state.src.calc = expr.value;
            syncCellsFromString();
            refresh('key');
            ae('key', { k: b.getAttribute('data-frc-key') });
        });
    });

    // примеры-чипы
    document.querySelectorAll('[data-frc-sample]').forEach((b) => {
        b.addEventListener('click', () => {
            const tab = b.closest('[data-pane]').getAttribute('data-pane');
            setSrc(tab, decText(b.getAttribute('data-frc-sample'), nt));
            funnel('sample', { tab });
            refresh('sample');
        });
    });

    // ещё такой же пример; недавние
    document.querySelectorAll('[data-frc-again]').forEach((b) => b.addEventListener('click', again));
    loadRecent();
    document.querySelectorAll('[data-frc-recent]').forEach((box) => {
        const tab = box.getAttribute('data-frc-recent');
        paintRecent(tab);
        box.addEventListener('click', (e) => {
            const item = e.target.closest('[data-frc-recent-item]');
            if (item) {
                setSrc(tab, item.getAttribute('data-frc-recent-item'));
                funnel('recent', { tab });
                refresh('recent');
                return;
            }
            if (e.target.closest('[data-frc-recent-clear]')) {
                recent[tab] = [];
                try { if (TS()) TS().save(SLUG, 'recent', recent); } catch (_) { /* без памяти */ }
                paintRecent(tab);
            }
        });
    });

    // клетки
    const cellsBox = $('frcCells');
    cellsBox.addEventListener('input', (e) => {
        const el = e.target;
        if (el.matches('input[data-k]')) {
            const i = +el.closest('.frc-cell').getAttribute('data-i');
            cells.operands[i][el.getAttribute('data-k')] = el.value;
            onCellsInput();
        }
    });
    cellsBox.addEventListener('change', (e) => {
        const el = e.target;
        if (el.matches('select[data-oi]')) {
            cells.ops[+el.getAttribute('data-oi')] = el.value;
            onCellsInput();
        }
    });
    cellsBox.addEventListener('keydown', (e) => {
        // «/» в клетке числителя — переход в знаменатель, как в тетради
        const el = e.target;
        if (!el.matches('input[data-k]')) return;
        if (e.key === '/' || (e.key === 'Enter' && el.getAttribute('data-k') !== 'd')) {
            e.preventDefault();
            const next = el.getAttribute('data-k') === 'w' ? el.parentElement.querySelector('[data-k="n"]') : el.parentElement.querySelector('[data-k="d"]');
            if (next) next.focus();
        }
    });
    cellsBox.addEventListener('click', (e) => {
        const del = e.target.closest('[data-del]');
        if (!del) return;
        const i = +del.getAttribute('data-del');
        cells.operands.splice(i, 1);
        cells.ops.splice(Math.max(0, i - 1), 1);     // у первого числа знак стоит ПОСЛЕ него
        paintCells();
        onCellsInput();
    });
    $('frcAdd').addEventListener('click', () => {
        if (cells.operands.length >= MAX_CELLS) return;
        cells.operands.push({ w: '', n: '', d: '' });
        cells.ops.push('+');
        paintCells();
        const last = document.querySelector('#frcCells .frc-cell:last-child [data-k="n"]');
        if (last) last.focus({ preventScroll: true });
        onCellsInput();
        ae('add_cell', { n: cells.operands.length });
    });
    $('frcClear').addEventListener('click', () => {
        setSrc('calc', '');
        refresh(false);
        expr.focus();
    });

    // способ, картинка, выдача
    $('frcMethods').addEventListener('click', (e) => {
        const b = e.target.closest('[data-frc-method]');
        if (!b) return;
        const key = b.getAttribute('data-frc-method-key');
        const id = b.getAttribute('data-frc-method');
        if (key === 'method') { saveSetting('method', id); return; }
        state[key] = id;
        ae('method', { key, id });
        refresh(false);
    });
    document.querySelectorAll('[data-frc-model]').forEach((b) => b.addEventListener('click', () => {
        state.model = b.getAttribute('data-frc-model');
        try { if (TS()) TS().save(SLUG, 'model', state.model); } catch (_) { /* без памяти */ }
        paintModel();
        ae('model', { m: state.model });
    }));
    $('frcCard').addEventListener('click', (e) => {
        const b = e.target.closest('[data-copy]');
        if (b) copyForm(b);
    });
    $('frcCopy').addEventListener('click', copySolution);
    $('frcPdf').addEventListener('click', savePdf);
    $('frcPrint').addEventListener('click', printSheet);
    $('frcShare').addEventListener('click', share);

    // настройки записи
    document.querySelectorAll('[data-frc-setting]').forEach((sel) => {
        sel.addEventListener('change', () => saveSetting(sel.getAttribute('data-frc-setting'), sel.value));
    });

    // задачи на дроби
    document.querySelectorAll('[data-frc-part]').forEach((b) => b.addEventListener('click', () => {
        state.part.kind = b.getAttribute('data-frc-part');
        const first = document.querySelector('[data-frc-part-sample][data-kind="' + state.part.kind + '"]');
        if (first) fillPart(first); else paintPart();
        funnel('mode', { tab: 'part', kind: state.part.kind });
        refresh('sample');
    }));
    document.querySelectorAll('[data-frc-part-sample]').forEach((b) => b.addEventListener('click', () => { fillPart(b); funnel('sample', { tab: 'part', kind: state.part.kind }); refresh('sample'); }));
    for (const [id, k] of [['frcPartA', 'a'], ['frcPartB', 'b']]) {
        const el = $(id);
        el.addEventListener('input', () => {
            state.part[k] = el.value;
            clearTimeout(timer);
            timer = setTimeout(() => refresh('typed'), 280);
        });
    }

    // тренажёр
    $('frcTopic').addEventListener('change', () => { state.trainer.topic = $('frcTopic').value; newTask(true); });
    document.querySelectorAll('[data-frc-level]').forEach((b) => b.addEventListener('click', () => {
        state.trainer.level = +b.getAttribute('data-frc-level');
        paintTrainerControls();
        newTask(true);
    }));
    $('frcTrNew').addEventListener('click', () => newTask(true));
    $('frcTrCheck').addEventListener('click', checkTask);
    $('frcTrShow').addEventListener('click', () => { showSolution(false); $('frcTrNext').hidden = false; });
    $('frcTrNext').addEventListener('click', () => newTask(true));
    ['frcAw', 'frcAn', 'frcAd'].forEach((id) => $(id).addEventListener('keydown', (e) => {
        // Enter: проверить; после верного ответа — следующий пример (серия без мыши)
        if (e.key === 'Enter') { e.preventDefault(); if (state.trainer.right) newTask(true); else checkTask(); }
        if (e.key === '/' && id === 'frcAn') { e.preventDefault(); $('frcAd').focus(); }
    }));
    $('frcStatsReset').addEventListener('click', () => { saveStats({}); paintStats(); funnel('trainer_reset', {}); });
    $('frcWsMake').addEventListener('click', makeWorksheet);
    const bindTS = (id, key) => { try { if (TS()) TS().bind(SLUG, $(id), key, $(id).type === 'checkbox' ? $(id).checked : $(id).value); } catch (_) { /* без памяти */ } };
    bindTS('frcStrict', 'strict');
    paintTrainerControls();

    // первый показ: ссылка или пример
    paintCells();
    if (!loadHash()) {
        setSrc('calc', decText(CFG.sample || '', nt));
        setTab('calc', { fromHash: true });
    }

    // Опора для стенда: состояние видно снаружи без чтения пикселей.
    window.__frcState = state;
    window.__frcText = () => (state.doc ? docText(state.doc, R, t) : '');
    window.__frcNotation = () => Object.assign({}, nt);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
